import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XIcon, SearchIcon, SparklesIcon } from 'lucide-react';
import { PlayCard } from './PlayCard';
import { FilterSection } from './FilterSection';
import { FeaturedPlays } from './FeaturedPlays';
interface GTMPlaysLibraryProps {
  isOpen: boolean;
  onClose: () => void;
}
export function GTMPlaysLibrary({
  isOpen,
  onClose
}: GTMPlaysLibraryProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPersona, setSelectedPersona] = useState<string[]>([]);
  const [selectedMotion, setSelectedMotion] = useState<string[]>([]);
  const [selectedStage, setSelectedStage] = useState<string[]>([]);
  const [selectedVertical, setSelectedVertical] = useState<string[]>([]);
  const plays = [{
    id: 1,
    name: 'High-Intent Account Blitz',
    description: 'Multi-channel outreach sequence targeting accounts showing 3+ buying signals within 7 days',
    expectedMetrics: 'Meeting rate +12-15% within 14 days',
    personas: ['VP Demand Gen', 'Sales Leader'],
    motion: 'ABM',
    stage: 'MOFU',
    vertical: 'SaaS',
    difficulty: 'Medium',
    timeToImplement: '3-5 days',
    isFeatured: true,
    performanceBadge: 'Top Performer'
  }, {
    id: 2,
    name: 'Product-Led Growth Activation',
    description: 'Automated engagement flow for trial users hitting key activation milestones',
    expectedMetrics: 'Trial-to-paid conversion +8-10% within 30 days',
    personas: ['CMO', 'VP Demand Gen'],
    motion: 'PLG',
    stage: 'MOFU',
    vertical: 'SaaS',
    difficulty: 'Easy',
    timeToImplement: '1-2 days',
    isFeatured: true,
    performanceBadge: 'Quick Win'
  }, {
    id: 3,
    name: 'Executive Champion Nurture',
    description: 'Personalized content journey for C-level prospects with industry-specific insights',
    expectedMetrics: 'Pipeline velocity +20-25% within 45 days',
    personas: ['CMO', 'Sales Leader'],
    motion: 'ABM',
    stage: 'TOFU',
    vertical: 'FinTech',
    difficulty: 'Hard',
    timeToImplement: '7-10 days',
    isFeatured: false,
    performanceBadge: null
  }, {
    id: 4,
    name: 'Inbound Lead Scoring & Routing',
    description: 'AI-powered lead qualification with intelligent territory assignment',
    expectedMetrics: 'Speed to lead -40% within 7 days',
    personas: ['RevOps', 'Sales Leader'],
    motion: 'Inbound',
    stage: 'TOFU',
    vertical: 'SaaS',
    difficulty: 'Medium',
    timeToImplement: '4-6 days',
    isFeatured: true,
    performanceBadge: 'High Impact'
  }, {
    id: 5,
    name: 'Healthcare Compliance Outreach',
    description: 'HIPAA-compliant multi-touch sequence for healthcare decision makers',
    expectedMetrics: 'Response rate +6-8% within 21 days',
    personas: ['VP Demand Gen', 'Sales Leader'],
    motion: 'Outbound',
    stage: 'TOFU',
    vertical: 'Healthcare',
    difficulty: 'Hard',
    timeToImplement: '5-7 days',
    isFeatured: false,
    performanceBadge: null
  }, {
    id: 6,
    name: 'Manufacturing Deal Acceleration',
    description: 'Technical validation workflow with ROI calculator for manufacturing prospects',
    expectedMetrics: 'Deal cycle time -15-20% within 60 days',
    personas: ['Sales Leader', 'RevOps'],
    motion: 'ABM',
    stage: 'BOFU',
    vertical: 'Manufacturing',
    difficulty: 'Medium',
    timeToImplement: '5-8 days',
    isFeatured: false,
    performanceBadge: null
  }];
  const filteredPlays = plays.filter(play => {
    const matchesSearch = play.name.toLowerCase().includes(searchQuery.toLowerCase()) || play.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPersona = selectedPersona.length === 0 || play.personas.some(p => selectedPersona.includes(p));
    const matchesMotion = selectedMotion.length === 0 || selectedMotion.includes(play.motion);
    const matchesStage = selectedStage.length === 0 || selectedStage.includes(play.stage);
    const matchesVertical = selectedVertical.length === 0 || selectedVertical.includes(play.vertical);
    return matchesSearch && matchesPersona && matchesMotion && matchesStage && matchesVertical;
  });
  const featuredPlays = plays.filter(play => play.isFeatured);
  const handlePlayClick = (playId: number) => {
    onClose();
    navigate('/plays/library');
  };
  return <>
      {/* Overlay */}
      {isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity" onClick={onClose} data-id="element-1240" />}
      {/* Sidebar */}
      <div className={`fixed right-0 top-0 h-full w-full lg:max-w-4xl bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-1241">
        <div className="h-full flex flex-col" data-id="element-1242">
          {/* Header */}
          <div className="border-b border-gray-200 px-4 lg:px-6 py-3 lg:py-4 flex items-center justify-between" data-id="element-1243">
            <div className="flex items-center gap-2 lg:gap-3" data-id="element-1244">
              <div className="w-6 h-6 lg:w-8 lg:h-8 bg-red-600 rounded flex items-center justify-center text-white font-bold text-xs lg:text-sm" data-id="element-1245">
                Z
              </div>
              <h2 className="text-base lg:text-xl font-semibold text-gray-900" data-id="element-1246">
                GTM Plays Library
              </h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1247">
              <XIcon className="w-5 h-5 text-gray-500" data-id="element-1248" />
            </button>
          </div>
          {/* Search Bar */}
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200" data-id="element-1249">
            <div className="relative" data-id="element-1250">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 lg:w-5 h-4 lg:h-5 text-gray-400" data-id="element-1251" />
              <input type="text" placeholder="Search plays..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 lg:pl-10 pr-10 lg:pr-12 py-2 lg:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm" data-id="element-1252" />
              <SparklesIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 lg:w-5 h-4 lg:h-5 text-red-500" data-id="element-1253" />
            </div>
          </div>
          {/* Filters */}
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200 overflow-x-auto" data-id="element-1254">
            <FilterSection selectedPersona={selectedPersona} setSelectedPersona={setSelectedPersona} selectedMotion={selectedMotion} setSelectedMotion={setSelectedMotion} selectedStage={selectedStage} setSelectedStage={setSelectedStage} selectedVertical={selectedVertical} setSelectedVertical={setSelectedVertical} data-id="element-1255" />
          </div>
          {/* Content */}
          <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 lg:py-6" data-id="element-1256">
            {/* Featured Plays */}
            {featuredPlays.length > 0 && searchQuery === '' && <FeaturedPlays plays={featuredPlays} data-id="element-1257" />}
            {/* All Plays */}
            <div className="mt-6" data-id="element-1258">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4" data-id="element-1259">
                {searchQuery ? 'Search Results' : 'All Plays'} (
                {filteredPlays.length})
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-1260">
                {filteredPlays.map(play => <PlayCard key={play.id} play={play} onClick={() => handlePlayClick(play.id)} data-id="element-1261" />)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}